// One masked reel column (docs/07 §2): blur while spinning, back-ease overshoot on stop,
// dithered peek rows above/below, twitch for Fate passes, one-step slides for nudges.
import { Container, Graphics, Sprite, TilingSprite, type Ticker } from 'pixi.js';
import { PALETTE } from '../../art/palette';
import { ditherTexture, getAtlas } from '../../art/rasterize';
import { clock } from '../timeline/clock';

export interface ReelOpts {
  index: number;
  cell: number; // logical px per cell (40 × zoom)
  zoom: number;
  rows: number;
  peek: number; // peek rows per side
}

const symTex = (id: string) => getAtlas().tex(`sym_${id}`);
const symBlur = (id: string, l: 1 | 2) => getAtlas().blur(`sym_${id}`, l);

export class Reel extends Container {
  readonly opts: ReelOpts;
  private strip = new Container();
  private sprites: Sprite[] = [];
  private column: string[] = [];
  private spinning = false;
  private spinSpeed = 0;
  private offset = 0;
  private pool: string[] = ['cherry'];
  private dimTop: TilingSprite;
  private dimBottom: TilingSprite;
  private holdFrame = new Graphics();
  private glow = new Graphics();
  /** The Auditor: the reel stays dark until it stops. */
  private dark = new Graphics();
  /** Night Shift: this column is a sticky Grin. */
  private wildFrame = new Graphics();
  /** Seized Reel hex. */
  private lockFrame = new Graphics();
  readonly peekPx: number;

  constructor(opts: ReelOpts) {
    super();
    this.opts = opts;
    const { cell, rows, peek } = opts;
    this.peekPx = peek > 0 ? Math.round(cell * (peek - 1) + cell * 0.4) : 0;
    const bg = new Graphics().rect(0, -this.peekPx, cell, rows * cell + this.peekPx * 2).fill(PALETTE.paper);
    const bgShade = new Graphics();
    for (let r = 0; r < rows; r++) bgShade.rect(0, r * cell + cell - 1, cell, 1).fill(PALETTE.steel);
    const mask = new Graphics().rect(0, -this.peekPx, cell, rows * cell + this.peekPx * 2).fill(0xffffff);
    this.addChild(bg, bgShade, this.strip, mask);
    this.strip.mask = mask;
    // rows + 2·peek visible cells, +2 spare for scrolling
    const count = rows + peek * 2 + 2;
    for (let i = 0; i < count; i++) {
      const s = new Sprite(symTex('cherry'));
      s.anchor.set(0.5);
      s.scale.set(opts.zoom);
      this.sprites.push(s);
      this.strip.addChild(s);
    }
    this.dimTop = new TilingSprite({ texture: ditherTexture(), width: cell, height: this.peekPx });
    this.dimTop.position.set(0, -this.peekPx);
    this.dimBottom = new TilingSprite({ texture: ditherTexture(), width: cell, height: this.peekPx });
    this.dimBottom.position.set(0, rows * cell);
    this.dark.rect(0, -this.peekPx, cell, rows * cell + this.peekPx * 2).fill(PALETTE.ink);
    this.dark.visible = false;
    this.addChild(this.dimTop, this.dimBottom, this.dark, this.glow, this.wildFrame, this.lockFrame, this.holdFrame);
    this.layout();
  }

  get cellSize(): number {
    return this.opts.cell;
  }

  /** Column defs: rows + 2·peek entries, index 0 = topmost peek. */
  setColumn(defs: string[]): void {
    // The 5×4/5×5 layouts show fewer peek rows than the sim reveals: trim both ends evenly.
    const want = this.opts.rows + 2 * this.opts.peek;
    const cut = defs.length > want ? Math.floor((defs.length - want) / 2) : 0;
    this.column = defs.slice(cut, cut + Math.max(want, defs.length - 2 * cut));
    this.offset = 0;
    this.layout();
  }

  /** Replace the visible rows (e.g. after on-land transforms). Returns rows that changed. */
  replaceRows(rowsDefs: string[]): number[] {
    const changed: number[] = [];
    rowsDefs.forEach((d, row) => {
      const idx = row + this.opts.peek;
      if (this.column[idx] !== d) {
        this.column[idx] = d;
        changed.push(row);
      }
    });
    if (changed.length && !this.spinning) this.layout();
    return changed;
  }

  setPool(defs: string[]): void {
    if (defs.length) this.pool = defs;
  }

  private layout(): void {
    const { cell, peek } = this.opts;
    const n = this.sprites.length;
    // sprite i shows column entry (i - 1): one spare above for scrolling in
    for (let i = 0; i < n; i++) {
      const s = this.sprites[i]!;
      const entry = i - 1;
      const y = (entry - peek) * cell + this.offset;
      s.position.set(cell / 2, y + cell / 2);
      if (!this.spinning) {
        const id = this.column[entry];
        s.texture = id ? symTex(id) : symTex(this.column[0] ?? 'cherry');
        s.visible = !!id;
      }
    }
  }

  private tick = (t: Ticker) => {
    if (!this.spinning) return;
    const { cell } = this.opts;
    this.offset += this.spinSpeed * t.deltaMS;
    while (this.offset >= cell) {
      this.offset -= cell;
      // shift textures down; a random pool symbol enters at the top
      for (let i = this.sprites.length - 1; i > 0; i--) this.sprites[i]!.texture = this.sprites[i - 1]!.texture;
      const id = this.pool[Math.floor(Math.random() * this.pool.length)]!;
      this.sprites[0]!.texture = symBlur(id, this.spinSpeed > 0.9 ? 2 : 1);
    }
    this.layout();
  };

  startSpin(): void {
    if (this.spinning) return;
    this.spinning = true;
    for (const s of this.sprites) {
      s.visible = true;
      s.alpha = 1;
    }
    for (let i = 0; i < this.sprites.length; i++) {
      const id = this.column[i - 1] ?? this.pool[0]!;
      this.sprites[i]!.texture = symBlur(id, 1);
    }
    this.spinSpeed = 0;
    const target = (this.opts.cell / 40) * 1.2; // px per ms
    clock.fx(this, 120, { spinSpeed: target, ease: 'power2.in' });
    TickerHub.add(this.tick);
  }

  /** Stop on the final column with a small overshoot (4 px × zoom) and back-ease settle. */
  async stop(column: string[], extraMs = 0): Promise<void> {
    if (extraMs > 0 && this.spinning) {
      this.spinSpeed *= 0.33;
      await clock.wait(extraMs);
    }
    this.spinning = false;
    TickerHub.remove(this.tick);
    this.setColumn(column);
    if (clock.isSkipping) return;
    const over = 4 * this.opts.zoom;
    this.offset = over;
    this.layout();
    await clock.tween(this, 90, {
      offset: 0,
      ease: 'back.out(3)',
      onUpdate: () => this.layout(),
    });
  }

  /** Fate pass: a quick re-spin to a new column. */
  async twitch(column: string[]): Promise<void> {
    if (clock.isSkipping) return this.setColumn(column);
    this.startSpin();
    this.spinSpeed = (this.opts.cell / 40) * 1.2;
    await clock.wait(80);
    await this.stop(column);
  }

  /** Slide one (or two) cells and settle on the new column. dir +1 = symbols move down. */
  async slide(column: string[], dir: 1 | -1, steps = 1): Promise<void> {
    const cell = this.opts.cell;
    this.setColumn(column);
    if (clock.isSkipping) return;
    this.offset = -dir * cell * steps;
    this.layout();
    await clock.tween(this, 160, { offset: 0, ease: 'back.out(1.7)', onUpdate: () => this.layout() });
  }

  /** Visible (non-peek) sprite for a row, for pulses and effects. */
  cellSprite(row: number): Sprite | undefined {
    return this.sprites[row + this.opts.peek + 1];
  }

  cellCenter(row: number): { x: number; y: number } {
    return { x: this.x + this.opts.cell / 2, y: this.y + row * this.opts.cell + this.opts.cell / 2 };
  }

  /** Fade the given visible rows (cells the machine ate); every other visible row is restored. */
  dimRows(rows: number[]): void {
    for (let row = 0; row < this.opts.rows; row++) {
      const s = this.cellSprite(row);
      if (s) s.alpha = rows.includes(row) ? 0.25 : 1;
    }
  }

  pulse(row: number): void {
    const s = this.cellSprite(row);
    if (!s || clock.isSkipping) return;
    const z = this.opts.zoom;
    s.scale.set(z * 1.15);
    clock.fx(s.scale, 250, { x: z, y: z, ease: 'back.out(2)' });
  }

  setHeld(held: boolean): void {
    const { cell, rows } = this.opts;
    this.holdFrame.clear();
    if (!held) return;
    const h = rows * cell;
    this.holdFrame.rect(0, 0, cell, h).stroke({ color: PALETTE.mint, width: 2, alignment: 1 });
    this.holdFrame.rect(0, h - 3, cell, 3).fill(PALETTE.mint);
  }

  setDark(on: boolean): void {
    this.dark.visible = on;
  }

  setWild(on: boolean): void {
    const { cell, rows } = this.opts;
    this.wildFrame.clear();
    if (on) this.wildFrame.rect(1, 1, cell - 2, rows * cell - 2).stroke({ color: PALETTE.lavender, width: 2, alignment: 1 });
  }

  setLocked(on: boolean): void {
    const { cell, rows } = this.opts;
    this.lockFrame.clear();
    if (!on) return;
    for (let y = 0; y < rows * cell; y += 6) this.lockFrame.rect(0, y, 2, 3).fill(PALETTE.red).rect(cell - 2, y, 2, 3).fill(PALETTE.red);
  }

  setGlow(on: boolean): void {
    const { cell, rows } = this.opts;
    this.glow.clear();
    if (on) this.glow.rect(-2, -2, cell + 4, rows * cell + 4).stroke({ color: PALETTE.gold, width: 2 });
  }

  /** Blink a peek row (near miss). */
  async blinkPeek(side: 'top' | 'bottom'): Promise<void> {
    const d = side === 'top' ? this.dimTop : this.dimBottom;
    for (let i = 0; i < 3; i++) {
      d.visible = false;
      await clock.wait(70);
      d.visible = true;
      await clock.wait(70);
    }
  }

  override destroy(): void {
    TickerHub.remove(this.tick);
    super.destroy({ children: true });
  }
}

/** Shared ticker registration so reels don't need an Application reference. */
export const TickerHub = {
  ticker: null as Ticker | null,
  add(fn: (t: Ticker) => void) {
    this.ticker?.add(fn);
  },
  remove(fn: (t: Ticker) => void) {
    this.ticker?.remove(fn);
  },
};
